import { useMemo } from 'react';
import { Box } from '@repo/ui/Box';
import { IconButton } from '@repo/ui/IconButton';
import { Popover } from '@repo/ui/Popover';
import { PopoverContentSimple } from '@repo/ui/Popover/PopoverContentSimple';
import { PopoverRoot } from '@repo/ui/Popover/PopoverRoot';
import { PopoverTrigger } from '@repo/ui/Popover/PopoverTrigger';
import { Separator } from '@repo/ui/Separator';
import { Flex } from '@repo/ui/Flex';

import type { OrderItem } from '../../../graphql/query/getOrder';
import { expandItemsWithClaims } from '../../../utils/expandItemsWithClaims';
import type { ShipmentGroup } from '../../../utils/item';
import s from './ItemSelectionTask.module.css';
import bucketStyles from './UnavailableForClaim.module.css';
import {
  ineligibleHeader,
  itemSelectionStepId,
} from '../../../layouts/ItemSelection/baseConfig';
import { rawString, Text } from '../../Language/Language';
import {
  ItemCardView,
  ItemCardButtonView,
  ClaimedItemCard,
  type StatusBadge,
} from '../../ItemCard/ItemCardView';
import { ShowMoreButton, showMoreCutoff } from './ShowMoreButton';
import { BucketHeader } from './BucketHeader';

/** Show more state for a single sub-bucket */
export interface SubBucketShowMoreState {
  showMore: boolean;
  onShowMore: () => void;
}

export interface UnavailableForClaimViewProps {
  /** Items with existing claims (claim history) */
  claimHistoryItems: OrderItem[];
  /** Items that are ineligible (no reasons/resolutions) */
  ineligibleItems: OrderItem[];
  /** Items not yet delivered (grouped by shipment) */
  notYetDeliveredGroups: ShipmentGroup[];
  isMobile: boolean;
  /** Show more state for claim history sub-bucket */
  claimHistoryShowMore: SubBucketShowMoreState;
  /** Show more state for ineligible items sub-bucket */
  ineligibleShowMore: SubBucketShowMoreState;
  /** Show more states for not-yet-delivered sub-buckets (one per shipment group) */
  notYetDeliveredShowMore: SubBucketShowMoreState[];
  onItemClick: (itemId: string) => void;
  onClaimClick?: (claimId: string) => void;
  orderNumber?: string;
  customerFirstName?: string;
  customerLastName?: string;
  /** Whether the user can override ineligible items (agent permission) */
  canOverride?: boolean;
}

/**
 * Presentational component for displaying items that are unavailable for claim.
 * Contains 3 sub-buckets: Claim History, Ineligible Items, Not Yet Delivered.
 * Each sub-bucket has its own "Show more" control.
 */
export const UnavailableForClaimView = ({
  claimHistoryItems,
  ineligibleItems,
  notYetDeliveredGroups,
  isMobile,
  claimHistoryShowMore,
  ineligibleShowMore,
  notYetDeliveredShowMore,
  onItemClick,
  onClaimClick,
  orderNumber,
  customerFirstName,
  customerLastName,
  canOverride = false,
}: UnavailableForClaimViewProps) => {
  // Check if we have any items to display
  const hasClaimHistory = claimHistoryItems.length > 0;
  const hasIneligible = ineligibleItems.length > 0;
  const hasNotYetDelivered = notYetDeliveredGroups.some(
    (group) => group.items.length > 0,
  );
  const hasAnyItems = hasClaimHistory || hasIneligible || hasNotYetDelivered;

  // Count how many sub-buckets have items - hide headers if only one
  const activeBucketCount =
    (hasClaimHistory ? 1 : 0) +
    (hasIneligible ? 1 : 0) +
    (hasNotYetDelivered ? 1 : 0);
  const showBucketHeaders = activeBucketCount > 1;

  // Expand claim history items into display items (showing each claim separately)
  const claimHistoryDisplayItems = useMemo(
    () =>
      expandItemsWithClaims(claimHistoryItems).filter(
        (item) => item.isClaimedItem,
      ),
    [claimHistoryItems],
  );

  // Expand ineligible items
  const ineligibleDisplayItems = useMemo(
    () => expandItemsWithClaims(ineligibleItems),
    [ineligibleItems],
  );

  const getIneligibleStatusBadges = (): StatusBadge[] => {
    const ineligibleLabel = rawString({
      langKey: 'Global.itemSelection.badge.ineligible',
    });
    const overrideLabel = canOverride
      ? rawString({ langKey: 'Global.itemSelection.badge.overrideAvailable' })
      : null;

    const badges: StatusBadge[] = [];

    if (ineligibleLabel) {
      badges.push({ label: ineligibleLabel, variant: 'ineligible' });
    }

    if (overrideLabel) {
      badges.push({ label: overrideLabel, variant: 'override' });
    }

    return badges;
  };

  if (!hasAnyItems) {
    return null;
  }

  return (
    <>
      <Box padding={['md:py-64', 'py-32']}>
        <Separator size="lg" />
      </Box>
      <Box padding={['px-24', 'md:px-0']}>
        {/* Main header: Unavailable for claim */}
        <Flex alignItems="center" className={s.headerContainer} gap="g-8">
          <h1 className={s[`${itemSelectionStepId}-${ineligibleHeader}`]}>
            <Text
              langKey="Global.itemSelection.UnavailableHeader"
              replacementTokens={{
                orderNumber: orderNumber ?? '',
                firstName: customerFirstName ?? '',
                lastName: customerLastName ?? '',
              }}
            />
          </h1>
          <PopoverRoot>
            <PopoverTrigger asChild={true}>
              <IconButton
                icon="question"
                label={rawString({
                  langKey: 'Global.help',
                })}
                variant="tertiary"
              />
            </PopoverTrigger>
            <Popover
              align={isMobile ? 'end' : 'center'}
              ariaLabel={rawString({
                langKey: 'Global.info',
              })}
              hasArrow={false}
            >
              <PopoverContentSimple
                closeButtonLabel={rawString({
                  langKey: 'Global.close',
                })}
              >
                <Text langKey="Global.itemSelection.UnavailableHeaderHelp" />
              </PopoverContentSimple>
            </Popover>
          </PopoverRoot>
        </Flex>

        {/* Sub-bucket: Claim History */}
        {hasClaimHistory && (
          <Box className={bucketStyles.subBucket}>
            {showBucketHeaders && <BucketHeader bucketType="claimHistory" />}
            <Flex direction="column" gap="g-16">
              {claimHistoryDisplayItems.map((displayItem, i) => {
                if (!claimHistoryShowMore.showMore && i > showMoreCutoff - 1)
                  return null;

                if (displayItem.singleClaim) {
                  return (
                    <ClaimedItemCard
                      key={displayItem.displayId}
                      displayItem={displayItem}
                      imageSize={isMobile ? 'sm' : 'xl'}
                      onClaimClick={onClaimClick}
                      ariaLabelledby={`${displayItem.displayId}-title`}
                    />
                  );
                }
                return null;
              })}
            </Flex>
            <ShowMoreButton
              onClick={claimHistoryShowMore.onShowMore}
              showMore={claimHistoryShowMore.showMore}
              hiddenItemCount={claimHistoryDisplayItems.length - showMoreCutoff}
            />
          </Box>
        )}

        {/* Sub-bucket: Ineligible Items */}
        {hasIneligible && (
          <Box className={bucketStyles.subBucket}>
            {showBucketHeaders && <BucketHeader bucketType="ineligible" />}
            <Flex direction="column" gap="g-16">
              {ineligibleDisplayItems.map((displayItem, i) => {
                if (!ineligibleShowMore.showMore && i > showMoreCutoff - 1)
                  return null;

                if (canOverride) {
                  return (
                    <ItemCardButtonView
                      key={displayItem.displayId}
                      aria-labelledby={`${displayItem.displayId}-title`}
                      imageSize={isMobile ? 'sm' : 'xl'}
                      item={displayItem}
                      onClick={() => onItemClick(displayItem.id)}
                      remainingQuantity={displayItem.quantity?.remaining || 1}
                      isShipmentScopeReason={false}
                      statusBadges={getIneligibleStatusBadges()}
                    />
                  );
                }

                return (
                  <ItemCardView
                    key={displayItem.displayId}
                    aria-labelledby={`${displayItem.displayId}-title`}
                    imageSize={isMobile ? 'sm' : 'xl'}
                    item={displayItem}
                    remainingQuantity={displayItem.quantity?.remaining || 1}
                    isShipmentScopeReason={false}
                    statusBadges={getIneligibleStatusBadges()}
                  />
                );
              })}
            </Flex>
            <ShowMoreButton
              onClick={ineligibleShowMore.onShowMore}
              showMore={ineligibleShowMore.showMore}
              hiddenItemCount={ineligibleDisplayItems.length - showMoreCutoff}
            />
          </Box>
        )}

        {/* Sub-bucket: Not Yet Delivered */}
        {hasNotYetDelivered &&
          notYetDeliveredGroups.map((group, groupIndex) => {
            if (group.items.length === 0) return null;

            // Fallback state if array indices are out of sync (defensive coding)
            const showMoreState = notYetDeliveredShowMore[groupIndex];
            if (!showMoreState) return null;

            return (
              <Box
                key={group.trackingNumber || groupIndex}
                className={bucketStyles.subBucket}
              >
                {showBucketHeaders && (
                  <BucketHeader
                    bucketType="notYetDelivered"
                    trackingNumber={group.trackingNumber}
                  />
                )}
                <Flex direction="column" gap="g-16">
                  {group.items.map((item, i) => {
                    if (!showMoreState.showMore && i > showMoreCutoff - 1)
                      return null;

                    return (
                      <ItemCardButtonView
                        key={item.id}
                        aria-labelledby={`${item.id}-title`}
                        imageSize={isMobile ? 'sm' : 'xl'}
                        item={item}
                        onClick={() => onItemClick(item.id)}
                        remainingQuantity={item.quantity?.remaining || 1}
                        isShipmentScopeReason={false}
                        statusBadges={[
                          {
                            label:
                              rawString({
                                langKey:
                                  'Global.itemSelection.badge.notYetDelivered',
                              }) ?? 'Not delivered',
                            variant: 'ineligible',
                          },
                        ]}
                      />
                    );
                  })}
                </Flex>
                <ShowMoreButton
                  onClick={showMoreState.onShowMore}
                  showMore={showMoreState.showMore}
                  hiddenItemCount={group.items.length - showMoreCutoff}
                />
              </Box>
            );
          })}
      </Box>
    </>
  );
};
